import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  toConfirmArrivalTarget,
  toConfirmWaybillTarget,
  type ConfirmArrivalTarget
} from '@/features/receive-confirm-arrival';
import { toReceiveConvoyTarget, type ReceiveConvoyTarget } from '@/features/receive-convoy';

import {
  useGetReceiveLocationsQuery,
  useLazyExportReceiveQuery,
  normalizeLocationRows,
  RECEIVE_LOCATION_TYPE,
  RECEIVE_MOVEMENT_TYPE,
  type ReceiveConfirmArrivalResultDto,
  type ReceiveLocationType,
  type ReceiveMovementType,
  type ReceiveQueryArgs,
  type ReceiveTableRow
} from '@/entities/receive';
import { useProjectSiteParams, usePermissions, useUrlState } from '@/shared/lib';

import type { ReceiveColumnHandlers } from './columns';
import { DEFAULT_URL_STATE } from './constants';

/** One card: the level-0 rows for a single `locationType`, plus its load state. */
export interface ReceiveLocationCard {
  locationType: ReceiveLocationType;
  locations: ReceiveTableRow[];
  consignmentCount: number;
}

export interface ReceiveMovementSection {
  movementType: ReceiveMovementType;
  cards: ReceiveLocationCard[];
  consignmentCount: number;
}

const countConsignments = (locations: ReceiveTableRow[]): number =>
  locations.reduce((total, location) => total + (location.consignmentCount ?? 0), 0);

export const useReceive = () => {
  const { t } = useTranslation();
  const { projectId, siteId, isValid } = useProjectSiteParams();
  const { canExportData, canEditData } = usePermissions();
  const [urlState] = useUrlState(DEFAULT_URL_STATE);

  const queryArgs: ReceiveQueryArgs = useMemo(
    () => ({
      projectId,
      siteId,
      consignmentNumber: urlState.consignmentNumber || undefined,
      organization: urlState.organization || undefined,
      originLocation: urlState.originLocation || undefined,
      shipmentIdentification: urlState.shipmentIdentification || undefined,
      search: urlState.search || undefined
    }),
    [projectId, siteId, urlState]
  );

  // One `/locations` call per card. These carry counts only — every nested packet
  // (shipments, then balances) is fetched by `useReceiveLazyRows` on expand.
  const internalTransfer = useGetReceiveLocationsQuery(
    { ...queryArgs, locationType: RECEIVE_LOCATION_TYPE.INTERNAL_TRANSFER },
    { skip: !isValid }
  );
  const transshipmentPoints = useGetReceiveLocationsQuery(
    { ...queryArgs, locationType: RECEIVE_LOCATION_TYPE.TRANSSHIPMENT_POINTS },
    { skip: !isValid }
  );
  const customerAddress = useGetReceiveLocationsQuery(
    { ...queryArgs, locationType: RECEIVE_LOCATION_TYPE.CUSTOMER_ADDRESS },
    { skip: !isValid }
  );
  const convoy = useGetReceiveLocationsQuery(
    { ...queryArgs, locationType: RECEIVE_LOCATION_TYPE.CONVOY },
    { skip: !isValid }
  );
  const customerDeliveries = useGetReceiveLocationsQuery(
    { ...queryArgs, locationType: RECEIVE_LOCATION_TYPE.CUSTOMER_DELIVERIES },
    { skip: !isValid }
  );

  const queries = useMemo(
    () => [internalTransfer, transshipmentPoints, customerAddress, convoy, customerDeliveries],
    [internalTransfer, transshipmentPoints, customerAddress, convoy, customerDeliveries]
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);
  const error = queries.find((query) => query.error)?.error;

  const buildCard = useCallback(
    (locationType: ReceiveLocationType, data: Parameters<typeof normalizeLocationRows>[0]): ReceiveLocationCard => {
      const locations = normalizeLocationRows(data ?? []);
      return { locationType, locations, consignmentCount: countConsignments(locations) };
    },
    []
  );

  const movementSections: ReceiveMovementSection[] = useMemo(() => {
    const internalCards = [
      buildCard(RECEIVE_LOCATION_TYPE.INTERNAL_TRANSFER, internalTransfer.data),
      buildCard(RECEIVE_LOCATION_TYPE.TRANSSHIPMENT_POINTS, transshipmentPoints.data),
      buildCard(RECEIVE_LOCATION_TYPE.CUSTOMER_ADDRESS, customerAddress.data),
      buildCard(RECEIVE_LOCATION_TYPE.CONVOY, convoy.data)
    ];
    const customerCards = [buildCard(RECEIVE_LOCATION_TYPE.CUSTOMER_DELIVERIES, customerDeliveries.data)];

    const sum = (cards: ReceiveLocationCard[]) => cards.reduce((total, card) => total + card.consignmentCount, 0);

    return [
      {
        movementType: RECEIVE_MOVEMENT_TYPE.INTERNAL_TRANSFERS,
        cards: internalCards,
        consignmentCount: sum(internalCards)
      },
      {
        movementType: RECEIVE_MOVEMENT_TYPE.CUSTOMER_DELIVERIES,
        cards: customerCards,
        consignmentCount: sum(customerCards)
      }
    ];
  }, [
    buildCard,
    internalTransfer.data,
    transshipmentPoints.data,
    customerAddress.data,
    convoy.data,
    customerDeliveries.data
  ]);

  const totalCount = useMemo(
    () => movementSections.reduce((total, section) => total + section.consignmentCount, 0),
    [movementSections]
  );

  // Identity of the current filter set — lazily loaded branches are scoped to it,
  // so a filter change drops them rather than leaving stale children behind.
  const filterKey = useMemo(() => JSON.stringify(queryArgs), [queryArgs]);

  const isEmpty = movementSections.every((section) => section.cards.every((card) => card.locations.length === 0));

  const [exportReceive, { isFetching: isExporting }] = useLazyExportReceiveQuery();

  const handleExport = useCallback(() => {
    exportReceive({ ...queryArgs, format: 'xlsx' })
      .unwrap()
      .then(() => toast.success(t('receive.dashboard.exportSuccess', 'Export downloaded successfully')))
      .catch(() => toast.error(t('receive.dashboard.exportError', 'Failed to export receive dashboard')));
  }, [exportReceive, queryArgs, t]);

  // The consignment (or waybill) currently being received; also the modal's mount condition,
  // so every open starts from a fresh form and a fresh context call.
  const [confirmTarget, setConfirmTarget] = useState<ConfirmArrivalTarget | null>(null);
  // The convoy currently being received, same mount-condition role.
  const [convoyTarget, setConvoyTarget] = useState<ReceiveConvoyTarget | null>(null);

  /**
   * Bumped after a successful receipt. Invalidating the `Receive` tag refetches
   * `/locations`, but the expanded branches live in `useReceiveLazyRows`' local
   * cache and would keep showing balances the receipt has just consumed — this
   * is the signal that drops them.
   */
  const [dataVersion, setDataVersion] = useState(0);

  // Deliberately does not close the modal: the receipt's summary (GRN numbers,
  // status changes) is only shown there, and the endpoint's echo is the only
  // place those numbers exist. The user closes it once they have read them.
  const handleConfirmSuccess = useCallback(() => {
    setDataVersion((version) => version + 1);
  }, []);

  /**
   * The receipt behind an open release form. *Receive and Release* swaps the
   * receipt modal for the release one; the receipt's summary moves along with
   * it, since its GRN numbers exist nowhere else.
   */
  const [releaseReceipt, setReleaseReceipt] = useState<ReceiveConfirmArrivalResultDto | null>(null);

  const handleReceiveAndRelease = useCallback((receipt: ReceiveConfirmArrivalResultDto) => {
    setConfirmTarget(null);
    setReleaseReceipt(receipt);
  }, []);

  const closeRelease = useCallback(() => setReleaseReceipt(null), []);

  const columnHandlers: ReceiveColumnHandlers = useMemo(
    () => ({
      onReceive: (row) => {
        const target = toConfirmArrivalTarget(row);
        if (!target) {
          toast.error(t('receive.confirm.error.noShipment', 'This row has no shipment to receive.'));
          return;
        }
        setConfirmTarget(target);
      },
      onReceiveWaybill: (row) => {
        const target = toConfirmWaybillTarget(row);
        if (target) setConfirmTarget(target);
      },
      onReceiveConvoy: (row) => {
        const target = toReceiveConvoyTarget(row);
        if (target) setConvoyTarget(target);
      },
      onViewDetails: () => toast(t('receive.dashboard.actions.comingSoon', 'Receive flow coming soon'))
    }),
    [t]
  );

  return {
    movementSections,
    projectId,
    siteId,
    filterKey,
    dataVersion,
    totalCount,
    isLoading,
    isFetching,
    isEmpty,
    error,
    isValid,
    canExport: canExportData,
    canReceive: canEditData,
    handleExport,
    isExporting,
    columnHandlers,
    confirmTarget,
    releaseReceipt,
    handleReceiveAndRelease,
    closeRelease,
    closeConfirm: () => setConfirmTarget(null),
    convoyTarget,
    closeConvoy: () => setConvoyTarget(null),
    handleConfirmSuccess
  };
};
